import { pagesPath } from "faqSrc/lib/$path";
import { useRouter } from "next/router";
import React from "react";
import styled from "styled-components";
import UserReactionIconButton from "~/components/modules/userReactionButtons/IconButton";
import {
  getUserReactionForSectionStoreKey,
  getUserReactionStoreKey,
} from "~/components/modules/userReactionButtons/UserReactionButtons";
import UserReactionWriteCommentButton, {
  HOW_TO_REVIEW_VISITED_STORE_KEY,
} from "~/components/modules/userReactionButtons/WriteCommentButton";
import { convertUrlObjectToString } from "~/lib/utils/urlHelper";
import { QuestionAnswer, UserReactionEnum } from "~/models/questionAnswer";
import { Topic } from "~/models/topic";

interface AmpUserReactionButtonsProps {
  increaseSmileReactionUrl?: string;
  increaseThumbsUpReactionUrl?: string;
  questionAnswerId: QuestionAnswer["id"];
  sectionId: string;
  sectionSmileCount: number | undefined;
  sectionThumbsUpCount: number | undefined;
  topicSlug: Topic["slug"];
}

const AmpUserReactionButtons: React.FC<AmpUserReactionButtonsProps> = ({
  increaseSmileReactionUrl,
  increaseThumbsUpReactionUrl,
  questionAnswerId,
  sectionId,
  sectionSmileCount = 0,
  sectionThumbsUpCount = 0,
  topicSlug,
}) => {
  const router = useRouter();
  const adjustedSmileCount = sectionSmileCount > 99 ? "99+" : sectionSmileCount;
  const adjustedThumbsUpCount =
    sectionThumbsUpCount > 99 ? "99+" : sectionThumbsUpCount;

  const scriptId = `${sectionId}-script`;
  const smileButtonId = `${sectionId}-smile`;
  const thumbsUpButtonId = `${sectionId}-thumbsUp`;
  const smileBadgeId = `${sectionId}-smile-badge`;
  const thumbsUpBadgeId = `${sectionId}-thumbsUp-badge`;
  const writeCommentButtonId = `${sectionId}-write-comment`;
  const reviewUrl = convertUrlObjectToString({
    locale:
      router?.locale !== router?.defaultLocale ? router?.locale : undefined,
    url: {
      ...pagesPath.qa
        ._topicSlug(topicSlug)
        ._questionAnswerId(questionAnswerId)
        .review.$url(),
    },
  });

  return (
    <Container>
      <amp-script
        script={scriptId}
        sandbox="allow-forms"
        width={296}
        height={58}
      >
        <ButtonGroup>
          <ButtonWrapper>
            <UserReactionIconButton
              badgeId={smileBadgeId}
              buttonId={smileButtonId}
              count={adjustedSmileCount}
              iconText="😄"
            />
          </ButtonWrapper>
          <ButtonWrapper>
            <UserReactionIconButton
              badgeId={thumbsUpBadgeId}
              buttonId={thumbsUpButtonId}
              count={adjustedThumbsUpCount}
              iconText="👍"
            />
          </ButtonWrapper>
          <ButtonWrapper>
            <UserReactionWriteCommentButton
              buttonId={writeCommentButtonId}
              questionAnswerId={questionAnswerId}
              topicSlug={topicSlug}
            />
          </ButtonWrapper>
        </ButtonGroup>
        <div
          dangerouslySetInnerHTML={{
            __html: `<script id="${scriptId}" type="text/plain" target="amp-script">
                    (() => {
                        const sectionId = "${sectionId}";
                        const smileButton = document.getElementById("${smileButtonId}");
                        const thumbsUpButton = document.getElementById("${thumbsUpButtonId}");
                        const smileBadge = document.getElementById("${smileBadgeId}");
                        const thumbsUpBadge = document.getElementById("${thumbsUpBadgeId}");
                        const writeCommentButton = document.getElementById("${writeCommentButtonId}");

                        const smileStoreKey = "${getUserReactionStoreKey({
                          questionAnswerId,
                          sectionId,
                          type: UserReactionEnum.smile,
                        })}";

                        const thumbsUpStoreKey = "${getUserReactionStoreKey({
                          questionAnswerId,
                          sectionId,
                          type: UserReactionEnum.thumbsUp,
                        })}";

                        const sectionStoreKey = "${getUserReactionForSectionStoreKey(
                          {
                            questionAnswerId,
                            sectionId,
                          }
                        )}"

                        let hasIncreasedSmile = getHasIncreasedFromLocalStorage(smileStoreKey);
                        let hasIncreasedThumbsUp = getHasIncreasedFromLocalStorage(thumbsUpStoreKey);
                        const hasVisitedHowToReviewPage = getStatusFromLocalStorage("${HOW_TO_REVIEW_VISITED_STORE_KEY}");

                        let smileCount = ${sectionSmileCount};
                        let thumbsUpCount = ${sectionThumbsUpCount};

                        if(hasIncreasedSmile){
                            smileButton.classList.add("isClicked");
                            smileCount = Math.max(smileCount, 1); // prevent count to be 0 when page is reloaded but not rebuilt
                        }

                        if(hasIncreasedThumbsUp){
                            thumbsUpButton.classList.add("isClicked");
                            thumbsUpCount = Math.max(thumbsUpCount, 1); // prevent count to be 0 when page is reloaded but not rebuilt
                        }

                        if (hasVisitedHowToReviewPage) {
                          writeCommentButton.setAttribute("href", "${reviewUrl}");
                        }

                        updateBadgeUI();

                        smileButton.addEventListener("click", onSmileClick);
                        thumbsUpButton.addEventListener("click", onThumbsUpClick);
                        writeCommentButton.addEventListener("click", onWriteCommentClick);

                        function onSmileClick() {
                            const currentHasIncreasedSmile = getHasIncreasedFromLocalStorage(smileStoreKey);
                            const increaseSmileReactionUrl = "${increaseSmileReactionUrl}";

                            if(increaseSmileReactionUrl && !currentHasIncreasedSmile && !hasIncreasedSmile && canIncreaseCount()){
                                fetch(increaseSmileReactionUrl, {
                                    method: "POST"
                                }).catch(error => console.error(error));
                                hasIncreasedSmile = true;
                                incrementSectionTotalIncrease();
                            }

                            if(currentHasIncreasedSmile){
                                smileButton.classList.remove("isClicked");
                                removeHasIncreased(smileStoreKey);
                                smileCount -= 1;
                            } else {
                                smileButton.classList.add("isClicked");
                                setHasIncreased(smileStoreKey);
                                smileCount += 1;
                            }
                            updateBadgeUI();
                        }

                        function onThumbsUpClick() {
                            const currentHasThumbsUpped = getHasIncreasedFromLocalStorage(thumbsUpStoreKey);
                            const increaseThumbsUpReactionUrl = "${increaseThumbsUpReactionUrl}";

                            if(increaseThumbsUpReactionUrl && !currentHasThumbsUpped && !hasIncreasedThumbsUp && canIncreaseCount()){
                                fetch(increaseThumbsUpReactionUrl, {
                                    method: "POST"
                                }).catch(error => console.error(error));
                                hasIncreasedThumbsUp = true;
                                incrementSectionTotalIncrease();
                            }

                            if(currentHasThumbsUpped){
                                thumbsUpButton.classList.remove("isClicked");
                                removeHasIncreased(thumbsUpStoreKey);
                                thumbsUpCount -= 1;
                            } else {
                                thumbsUpButton.classList.add("isClicked");
                                setHasIncreased(thumbsUpStoreKey);
                                thumbsUpCount += 1;
                            }
                            updateBadgeUI();
                        }

                        function onWriteCommentClick() {
                          setStatus("${HOW_TO_REVIEW_VISITED_STORE_KEY}", "1")
                        }

                        function updateBadgeUI() {
                            smileBadge.textContent = smileCount > 99 ? "99+" : Math.max(smileCount, 0).toString();
                            thumbsUpBadge.textContent = thumbsUpCount > 99 ? "99+" : Math.max(thumbsUpCount, 0).toString();
                        }

                        function setHasIncreased(key){
                            localStorage.setItem(key, "1");
                        }

                        function removeHasIncreased(key){
                            localStorage.setItem(key, "0");
                        }

                        function getHasIncreasedFromLocalStorage(key){
                            const value = localStorage.getItem(key);

                            if(value && value !== "0"){
                                return true;
                            }

                            return false;
                        }

                        function incrementSectionTotalIncrease() {
                            const currentNumber = getSectionTotalIncrease();

                            const incrementedNumber = currentNumber + 1;

                            localStorage.setItem(sectionStoreKey, incrementedNumber.toString());
                        }

                        function getSectionTotalIncrease() {
                            const currentValue = localStorage.getItem(sectionStoreKey);

                            const currentNumber = parseInt(currentValue || 0);

                            return currentNumber;
                        }

                        function canIncreaseCount() {
                            return getSectionTotalIncrease() < 3;
                        }

                        function setStatus(key, value) {
                          localStorage.setItem(key, value);
                        }

                        function getStatusFromLocalStorage(key) {
                          const value = localStorage.getItem(key);

                          if (value && value !== "0") {
                            return true;
                          }

                          return false;
                        }
                    })();
                  </script>`,
          }}
        />
      </amp-script>
    </Container>
  );
};

const Container = styled.div`
  align-items: center;
  display: grid;
  justify-content: flex-end;
  margin-top: 24px;
`;

const ButtonGroup = styled.div`
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  justify-content: flex-end;
`;

const ButtonWrapper = styled.span`
  margin: 0 4px;
`;

export default AmpUserReactionButtons;
